/**
 * Canonical (de)serialization for the Better TDL on-disk format (".tdl").
 *
 * Format v2 ("flat v2.1") — a flat, id-keyed map that mirrors the in-memory
 * `TodoTree`, so save/load needs no structural conversion:
 *
 *   {
 *     "format": "better-tdl",
 *     "version": 2,
 *     "root": "<root-uuid>",
 *     "today": { "date": "2026-08-02", "items": ["<uuid>"] },
 *     "nodes": {
 *       "<uuid>": { "name": "...", "priority": "high", "children": ["<uuid>"] },
 *       ...
 *     }
 *   }
 *
 * Design goals (see CLAUDE.md / the format design discussion):
 * - Sparse: every field except `name` is omitted when it equals its default.
 * - Canonical: `nodes` keys are sorted lexicographically and each node's keys
 *   are emitted in a fixed order, so a no-op load -> save is byte-identical
 *   (stable git/Dropbox diffs). `children` arrays carry sibling order and are
 *   never sorted.
 * - Versioned: a top-level `version` enables clean migration. Legacy unversioned
 *   nested files are treated as "v1" and migrated on load.
 *
 * The flat internal `TodoTree` is unchanged; this module only converts between
 * it and the serialized document.
 */

import {
  TodoTree,
  TodoItem,
  TodoLink,
  TodayList,
  Priority,
  Visibility,
  DEFAULT_TODO_ITEM,
  findRootNodeId,
  isNestedTodoTree,
  validateNestedTodoTree,
} from '../types/todo';
import { nestedToFlatTreeForInternalUse } from './todoTemplate';
import { sanitizeTodayList } from './today';
import { validateTodoTreeIntegrity } from './validation';
import { FILES } from './constants';

/** Identifies the file as a Better TDL document (disambiguates from generic JSON). */
export const TDL_FORMAT_TAG = 'better-tdl';

/** Current on-disk schema version. Bump on a breaking format change. */
export const STORAGE_FORMAT_VERSION = 2;

/** A single node as written to disk: required `name`, everything else sparse. */
export interface SerializedNode {
  name: string;
  priority?: Priority;
  visibility?: Visibility;
  done?: boolean;
  blocked?: boolean;
  archived?: boolean;
  highlighted?: boolean;
  timeEstimate?: number;
  description?: string;
  links?: TodoLink[];
  children?: string[];
}

/** The day plan as written to disk (omitted entirely when there is none). */
export interface SerializedToday {
  date: string;
  items: string[];
}

/** Top-level document wrapper. */
export interface TdlDocument {
  format: string;
  version: number;
  root: string;
  today?: SerializedToday;
  nodes: Record<string, SerializedNode>;
}

export interface LoadResult {
  tree?: TodoTree;
  rootId?: string;
  today?: TodayList;
  error?: string;
}

/**
 * Convert an in-memory item to its sparse serialized form.
 *
 * Keys are inserted in a fixed order (name, then flags/fields, then `children`
 * last) so JSON.stringify emits deterministic, readable diffs.
 */
function toSerializedNode(item: TodoItem): SerializedNode {
  const node: SerializedNode = { name: item.name };

  if (item.priority && item.priority !== DEFAULT_TODO_ITEM.priority) node.priority = item.priority;
  if (item.visibility && item.visibility !== DEFAULT_TODO_ITEM.visibility) node.visibility = item.visibility;
  if (item.done) node.done = true;
  if (item.blocked) node.blocked = true;
  if (item.archived) node.archived = true;
  if (item.highlighted) node.highlighted = true;
  if (typeof item.timeEstimate === 'number') node.timeEstimate = item.timeEstimate;
  if (item.description && item.description.length > 0) node.description = item.description;
  if (item.links && item.links.length > 0) {
    node.links = item.links.map(link => ({ to: link.to, type: link.type }));
  }
  if (item.children && item.children.length > 0) node.children = [...item.children];

  return node;
}

/**
 * Serialize a flat TodoTree to the canonical v2 JSON string.
 *
 * Output is deterministic: `nodes` keys sorted lexicographically, fixed
 * per-node key order, 2-space indent, trailing newline.
 *
 * `today` is written only when it holds at least one task that exists in the
 * tree, keeping the field absent for the common case of no day plan. Its item
 * order is the user's ranking and is never sorted.
 */
export function serializeTree(tree: TodoTree, rootId?: string, today?: TodayList): string {
  const root = rootId ?? findRootNodeId(tree) ?? '';

  const nodes: Record<string, SerializedNode> = {};
  for (const id of Object.keys(tree).sort()) {
    nodes[id] = toSerializedNode(tree[id]);
  }

  const todayItems = today && today.date ? today.items.filter(id => tree[id]) : [];

  const doc: TdlDocument = {
    format: TDL_FORMAT_TAG,
    version: STORAGE_FORMAT_VERSION,
    root,
    ...(todayItems.length > 0 ? { today: { date: today!.date, items: todayItems } } : {}),
    nodes,
  };

  return JSON.stringify(doc, null, FILES.JSON_INDENT_SPACES) + '\n';
}

/**
 * Expand a serialized document into a full flat TodoTree, filling defaults for
 * every omitted field. This MUST run before validation, because the validators
 * iterate `item.children` directly and a sparse leaf has no `children` key.
 */
function serializedDocumentToFlat(doc: TdlDocument): TodoTree {
  const tree: TodoTree = {};

  for (const [id, node] of Object.entries(doc.nodes)) {
    const item: TodoItem = {
      name: node.name,
      children: Array.isArray(node.children) ? [...node.children] : [],
      archived: node.archived ?? DEFAULT_TODO_ITEM.archived,
      done: node.done ?? DEFAULT_TODO_ITEM.done,
      blocked: node.blocked ?? DEFAULT_TODO_ITEM.blocked,
      priority: node.priority ?? DEFAULT_TODO_ITEM.priority,
      visibility: node.visibility ?? DEFAULT_TODO_ITEM.visibility,
      description: node.description ?? DEFAULT_TODO_ITEM.description,
      highlighted: node.highlighted ?? DEFAULT_TODO_ITEM.highlighted,
    };

    if (typeof node.timeEstimate === 'number') item.timeEstimate = node.timeEstimate;
    if (Array.isArray(node.links) && node.links.length > 0) {
      item.links = node.links.map(link => ({ to: link.to, type: link.type }));
    }

    tree[id] = item;
  }

  return tree;
}

/**
 * Structural type guard for a v2+ document. Detection keys on `version` + `nodes`
 * (the `format` tag is written but not required, to stay lenient on input).
 */
export function isTdlDocument(data: any): data is TdlDocument {
  return (
    !!data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    typeof data.version === 'number' &&
    !!data.nodes &&
    typeof data.nodes === 'object' &&
    !Array.isArray(data.nodes)
  );
}

/**
 * Validate a fully-expanded tree on load: reference integrity + cycle detection
 * (via the shared integrity check), the single-parent invariant the app relies
 * on, and that any cross-links resolve. Cross-links may form cycles (legal for
 * dependencies), so they are reference-checked but not cycle-checked.
 */
function validateLoadedTree(tree: TodoTree): { isValid: boolean; error?: string } {
  const integrity = validateTodoTreeIntegrity(tree);
  if (!integrity.isValid) {
    return { isValid: false, error: integrity.errors[0] };
  }

  const refCount = new Map<string, number>();
  for (const item of Object.values(tree)) {
    for (const childId of item.children) {
      refCount.set(childId, (refCount.get(childId) ?? 0) + 1);
    }
  }
  for (const [id, count] of Array.from(refCount.entries())) {
    if (count > 1) {
      const name = tree[id]?.name ?? id;
      return { isValid: false, error: `Node "${name}" (${id}) is referenced by multiple parents` };
    }
  }

  for (const item of Object.values(tree)) {
    if (!item.links) continue;
    for (const link of item.links) {
      if (!tree[link.to]) {
        return { isValid: false, error: `Link in "${item.name}" references non-existent node: ${link.to}` };
      }
    }
  }

  return { isValid: true };
}

/**
 * Parse and validate any supported .tdl content into a flat TodoTree.
 *
 * Accepts the current v2 format and the legacy unversioned nested format
 * ("v1"), migrating the latter. Rejects files written by a newer schema
 * version rather than silently dropping data.
 */
export function loadTdlContent(content: string): LoadResult {
  let parsed: any;
  try {
    parsed = JSON.parse(content);
  } catch (error) {
    return { error: error instanceof Error ? `Invalid JSON: ${error.message}` : 'Invalid JSON' };
  }

  // Current format (v2+): { format, version, root, nodes }
  if (isTdlDocument(parsed)) {
    if (parsed.version > STORAGE_FORMAT_VERSION) {
      return {
        error: `This file was created by a newer version of Better TDL (format v${parsed.version}). Please update the app to open it.`,
      };
    }

    const tree = serializedDocumentToFlat(parsed);
    const validation = validateLoadedTree(tree);
    if (!validation.isValid) return { error: validation.error };

    const rootId =
      typeof parsed.root === 'string' && tree[parsed.root]
        ? parsed.root
        : findRootNodeId(tree) ?? undefined;

    // A malformed or stale day plan is dropped rather than failing the load —
    // it is optional metadata, not part of the task data itself.
    return { tree, rootId, today: sanitizeTodayList(parsed.today, tree) };
  }

  // Legacy nested format (unversioned "v1"): single root key, wrapped children.
  if (isNestedTodoTree(parsed)) {
    const nestedValidation = validateNestedTodoTree(parsed);
    if (!nestedValidation.isValid) return { error: nestedValidation.error };

    const tree = nestedToFlatTreeForInternalUse(parsed);
    const validation = validateLoadedTree(tree);
    if (!validation.isValid) return { error: validation.error };

    return { tree, rootId: findRootNodeId(tree) ?? undefined };
  }

  return {
    error:
      'Unrecognized file format. Expected a Better TDL document with "version" and "nodes", or a legacy nested tree.',
  };
}
